import { CloudDownload, Delete } from '@mui/icons-material';
import {
  Box,
  FormControlLabel,
  Switch,
  darken,
  Divider,
  Grid,
  Theme,
  useMediaQuery
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { format, parseISO } from 'date-fns';
import { useSnackbar } from 'notistack';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import {
  Button as SMButton,
  Link,
  Page,
  PageTitleWithBack,
  Pagination,
  Table
} from '../../../components';
import { deleteUndefinedPart, getUndefinedParts } from '../../../fetch';
import { IUndefinedPart } from '../../../models';
import { MobileUndefinedPart } from './mobile-undefined-parts';
import { logError } from '../../../services';

export const ManageUndefinedParts = () => {
  const { enqueueSnackbar } = useSnackbar();
  const isMobile = useMediaQuery('(max-width: 960px)');
  const classes = useStyles();

  const [showOnlyUsedInOrders, setShowOnlyUsedInOrders] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isDownloadingCSV, setIsDownloadingCSV] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);
  const [selectedSort, setSelectedSort] = useState<string>('description');
  const [sortDirection, setSortDirection] = useState<{
    description?: 'Asc' | 'Desc';
    createdBy?: 'Asc' | 'Desc';
  }>({
    description: 'Asc'
  });
  const [undefinedParts, setUndefinedParts] = useState<IUndefinedPart[]>([]);

  const fetchUndefinedParts = async () => {
    try {
      setIsLoading(true);
      const res = await getUndefinedParts({
        page: page + 1,
        sortBy: selectedSort,
        sortDirection: sortDirection[selectedSort] as string,
        perPage: perPage,
        showOnlyUsedInOrders
      });
      setRecordCount(res.totalRecordCount);
      setUndefinedParts(res.records);
    } catch (error) {
      enqueueSnackbar(`Error loading undefined parts, please try again.`, {
        variant: 'error'
      });
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };
  const handleClickColumn = (column: string) => {
    setSelectedSort(column);
    setSortDirection({
      ...sortDirection,
      [column]: sortDirection[column] === 'Asc' ? 'Desc' : 'Asc'
    });
  };
  const handleDelete = async id => {
    const result = window.confirm('Are you sure you want to delete this undefined part?');
    if (result) {
      setIsDeleting(true);
      try {
        await deleteUndefinedPart(id);
        fetchUndefinedParts();
        enqueueSnackbar('Undefined Part Deleted!', {
          variant: 'success'
        });
      } catch (error) {
        const errorMessage = error?.response?.data?.Detail;
        enqueueSnackbar(
          errorMessage ? errorMessage : 'Error deleting undefined part, please try again.',
          {
            variant: 'error'
          }
        );
      } finally {
        setIsDeleting(false);
      }
    }
  };
  useEffect(() => {
    fetchUndefinedParts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage, showOnlyUsedInOrders, sortDirection, selectedSort]);

  const columns = useMemo(() => {
    return [
      {
        Header: 'Description',
        accessor: 'description',
        isServerSorted: selectedSort === 'description',
        isServerSortedDesc: sortDirection.description === 'Desc',
        handleClickColumn: () => handleClickColumn('description')
      },
      {
        Header: 'Order Number(s)',
        accessor: 'orderNumbers',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }) => {
          const orderNumbers = original.orderNumbers.map((orderNumber, index) => {
            const seperator = index !== 0 ? ', ' : '';
            return (
              <Fragment key={index}>
                {seperator}
                <Link
                  className={classes.orderNumberLink}
                  to={`/production/orders/order-management/${orderNumber}`}
                >
                  {orderNumber}
                </Link>
              </Fragment>
            );
          });
          return <p>{orderNumbers}</p>;
        }
      },
      {
        Header: 'Created By',
        accessor: 'createdBy',
        isServerSorted: selectedSort === 'createdBy',
        isServerSortedDesc: sortDirection.createdBy === 'Desc',
        handleClickColumn: () => handleClickColumn('createdBy')
      },
      {
        id: 'actions',
        accessor: '',
        Header: '',
        sort: false,

        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: any } };
        }) => {
          return (
            <div className={classes.actionButtons}>
              <SMButton
                id="delete"
                className={classes.deleteButton}
                startIcon={<Delete />}
                loading={isDeleting ? 'Deleting...' : ''}
                onClick={() => {
                  handleDelete(original.undefinedPartDescriptionId);
                }}
              >
                Delete
              </SMButton>
            </div>
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [undefinedParts]);

  const formatCSVColumns = (undefinedParts: IUndefinedPart[], csv: string) => {
    undefinedParts.forEach(row => {
      if (row.description) {
        csv += `${row.description.replace(/,/g, '')},`;
      } else {
        csv += `,`;
      }

      if (row.orderNumbers) {
        csv += `"${row.orderNumbers.join(', ')}",`;
      } else {
        csv += `,`;
      }

      if (row.createdBy) {
        csv += `${row.createdBy.trim()}`;
      } else {
        csv += ``;
      }
      csv += '\n';
    });
    return csv;
  };
  return (
    <>
      <Page title="Manage Undefined Parts">
        <Grid container>
          <Grid item xs={12} className={classes.pageTitleWrapper}>
            <PageTitleWithBack
              showDivider={false}
              backText="Back to Admin home"
              backUrl="/admin"
              title="Manage Undefined Parts"
              additionalHeaderContent={
                <Box>
                  <FormControlLabel
                    control={
                      <Switch
                        color="primary"
                        checked={showOnlyUsedInOrders}
                        id="active-inactive"
                        disabled={isLoading}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
                          setPage(0);
                          setShowOnlyUsedInOrders(checked);
                        }}
                      />
                    }
                    label="Show w/ Orders Only"
                  />
                  <LoadingButton
                    loading={isDownloadingCSV}
                    loadingPosition="start"
                    id={'csv_download'}
                    color="primary"
                    variant="contained"
                    disabled={isLoading || isDownloadingCSV}
                    onClick={async () => {
                      try {
                        setIsDownloadingCSV(true);
                        const BOM = String.fromCharCode(0xfeff);
                        // Initialize CSV with headers
                        let csv = 'Description, Order Number(s), Created By\n';

                        const res = await getUndefinedParts({
                          page: 1,
                          sortBy: selectedSort,
                          sortDirection: sortDirection[selectedSort] as string,
                          perPage: null,
                          showOnlyUsedInOrders: false
                        });
                        // Add order values to the csv
                        // If the rows are active, remove Order No. from CSV
                        csv = formatCSVColumns(res.records, csv);

                        // Create an file the user can download
                        const url = window.URL.createObjectURL(
                          new Blob([`${BOM}${csv}`], { type: 'text/csv' })
                        );
                        const el = document.createElement('a');
                        el.href = url;
                        el.target = '_blank';
                        el.download = `undefinedParts_${format(
                          parseISO(new Date().toISOString()),
                          'yyyy_LL_dd_hh_mm_ss'
                        )}.csv`;
                        // Download the file
                        el.click();
                      } catch (error) {
                        logError(error, 'Error downloading CSV');
                      } finally {
                        setIsDownloadingCSV(false);
                      }
                    }}
                    startIcon={<CloudDownload />}
                  >
                    CSV
                  </LoadingButton>
                </Box>
              }
            />
          </Grid>
        </Grid>
        <Divider />
        <Table
          columns={columns}
          data={undefinedParts}
          isLoading={isLoading}
          hidePagination
          noResultsText="No Undefined Parts Found"
          ResponsiveComponent={isMobile ? MobileUndefinedPart : undefined}
          mobileProps={{
            handleDelete: (undefinedPart: IUndefinedPart) =>
              handleDelete(undefinedPart.undefinedPartDescriptionId)
          }}
        />
        {!isLoading && (
          <Pagination
            page={page}
            count={recordCount}
            rowsPerPage={perPage}
            setPage={setPage}
            setRowsPerPage={setRowsPerPage}
          />
        )}
      </Page>
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  wrapper: {
    marginTop: '1rem'
  },
  pageTitleWrapper: {
    '@media (min-width: 768px)': {
      flex: 1
    }
  },
  infoWrapper: {
    '@media (min-width: 768px)': {
      textAlign: 'right'
    }
  },
  deleteButton: {
    marginLeft: theme.spacing(0.5),
    color: theme.palette.error.main
  },
  orderNumberLink: {
    color: darken(theme.palette.primary.main, 0.3),
    '&:hover, &:focus': {
      'text-decoration': 'underline'
    }
  },
  actionButtons: {
    textAlign: 'right'
  }
}));
